import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { fetchReportProfitLoss } from '@/lib/api/reports.js';
import { exportToCsv } from '@/lib/csv-export.js';
import { formatCurrency } from '@/lib/format.js';
import { useReportQueryParams } from '@/pages/reports/report-filters-context.js';

export function ProfitLossReportPage() {
  const qBuilt = useReportQueryParams();

  const q = useQuery({
    queryKey: ['reports-profit-loss', qBuilt],
    queryFn: () => fetchReportProfitLoss(qBuilt),
  });

  const d = q.data;

  const chartRows = useMemo(
    () =>
      (d?.chart ?? []).map((r) => ({
        ...r,
        dLabel: r.date.length > 10 ? r.date.slice(0, 10) : r.date,
        revenueNum: Number(r.revenue),
        expensesNum: Number(r.expenses),
        netProfitNum: Number(r.netProfit),
      })),
    [d],
  );

  const csv = () => {
    if (!d) return;
    exportToCsv(
      d.chart.map((r) => ({ date: r.date, revenue: r.revenue, expenses: r.expenses, netProfit: r.netProfit })),
      'profit-loss.csv',
    );
  };

  if (q.isLoading) return <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Loading profit &amp; loss…</div>;
  if (q.isError || !d?.revenue || !d?.cogs || !d?.chart || !d?.expenses) {
    return <div className="rounded-2xl border border-red-100 bg-red-50 p-8 text-red-800">Could not load profit &amp; loss report.</div>;
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-end">
        <button type="button" className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold hover:bg-slate-50" onClick={csv}>
          Export CSV
        </button>
      </div>

      {d.costCompleteness?.message ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          {d.costCompleteness.message}
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: 'Total revenue', v: formatCurrency(d.revenue.total) },
          { label: 'COGS (parts)', v: formatCurrency(d.cogs.total) },
          { label: 'Gross profit', v: formatCurrency(d.grossProfit), sub: `${d.grossMarginPct.toFixed(1)}% margin` },
          { label: 'Net profit', v: formatCurrency(d.netProfit), sub: `${d.netMarginPct.toFixed(1)}% margin` },
        ].map((k) => (
          <div key={k.label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-medium text-slate-500">{k.label}</p>
            <p className="mt-1 text-xl font-bold text-slate-950">{k.v}</p>
            {k.sub ? <p className="mt-0.5 text-xs text-slate-500">{k.sub}</p> : null}
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-950">Revenue breakdown</h3>
          <ul className="mt-4 space-y-2 text-sm">
            <li className="flex justify-between"><span>Repairs</span><span className="font-semibold">{formatCurrency(d.revenue.repairs)}</span></li>
            <li className="flex justify-between"><span>Retail / accessories</span><span className="font-semibold">{formatCurrency(d.revenue.retail)}</span></li>
            <li className="flex justify-between"><span>Other</span><span className="font-semibold">{formatCurrency(d.revenue.other)}</span></li>
            <li className="flex justify-between border-t border-slate-200 pt-2 font-bold"><span>Total</span><span>{formatCurrency(d.revenue.total)}</span></li>
          </ul>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-950">Operating expenses</h3>
          <ul className="mt-4 space-y-2 text-sm">
            {d.expenses.map((e) => (
              <li key={e.category} className="flex justify-between">
                <span className="capitalize">{e.category.replace(/_/g, ' ')}</span>
                <span className="font-semibold">{formatCurrency(e.amount)}</span>
              </li>
            ))}
            {d.expenses.length === 0 ? <li className="text-slate-500">No expenses recorded.</li> : null}
            <li className="flex justify-between border-t border-slate-200 pt-2 font-bold"><span>Total</span><span>{formatCurrency(d.totalExpenses)}</span></li>
          </ul>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-sm font-bold text-slate-950">Revenue vs expenses over time</h3>
        <div className="mt-4 h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartRows}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="dLabel" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v: number) => formatCurrency(v)} />
              <Legend />
              <Bar dataKey="revenueNum" name="Revenue" fill="#22c55e" radius={[4, 4, 0, 0]} />
              <Bar dataKey="expensesNum" name="Expenses" fill="#ef4444" radius={[4, 4, 0, 0]} />
              <Bar dataKey="netProfitNum" name="Net profit" fill="#6366f1" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
